import type { PlantacionDeInforme } from "@/lib/informes";
import type { PlantacionPropuesta } from "@/lib/rellenoConIa";
import { textoDeDecimal, textoDeNumero } from "@/lib/montos";

import { claveDeEspecie, type FormularioDePlantacion } from "./plantaciones";

/* ---------------------------------------------------------------------------
   "Rellenar con IA" en "Descriptor de plantaciones": traducir lo que propuso el
   modelo al formulario de la pestaña.

   ES EL CUARTO GEMELO de maquinaria, instalaciones y construcciones, y existe
   por lo mismo: esta pestaña tampoco tiene borrador —de cada cuartel cuelgan
   fotos, así que cada fila se crea con su propio POST— y el riesgo del punto de
   miles sigue vivo, aunque acá no haya dinero: una superficie mal leída se
   multiplica por la densidad y el total de plantas del predio sale falso.
--------------------------------------------------------------------------- */

/**
 * Una plantación propuesta, con la forma que tiene el formulario.
 *
 * NO TIENE NINGÚN DEFAULT QUE APLICAR: el request solo exige la especie, y esa
 * viene del modelo o la fila se descartó en el servidor.
 *
 * `nota_terreno` VA VACÍA SIEMPRE: el servidor no la propone —es lo que anotó
 * quien recorrió el cuartel— así que acá no hay nada que traducir.
 *
 * LAS PLANTAS TOTALES NO SE TRADUCEN porque no existen en la propuesta: la
 * cuenta es superficie × densidad con redondeo POR FILA y la hace el servidor
 * al guardar. El formulario tampoco tiene ese campo.
 */
export function formularioDesdeLaPlantacionPropuesta(
  plantacion: PlantacionPropuesta,
): FormularioDePlantacion {
  return {
    especie: plantacion.especie,
    variedad: plantacion.variedad ?? "",
    anio_plantacion: textoDeNumero(plantacion.anio_plantacion),
    superficie_ha: decimal(plantacion.superficie_ha),
    cuartel: plantacion.cuartel ?? "",
    portainjerto: plantacion.portainjerto ?? "",
    // TEXTO Y NADA MÁS: "4,5 x 2,0" se escribe tal cual. No pasa por
    // `textoDeDecimal` —no es un número— ni se le toca el separador.
    distancia_plantacion: plantacion.distancia_plantacion ?? "",
    plantas_por_ha: textoDeNumero(plantacion.plantas_por_ha),
    sistema_riego: plantacion.sistema_riego ?? "",
    nota_terreno: "",
  };
}

/**
 * ¿Esta plantación ya está en el cuadro?
 *
 * NO SIRVE PARA ESCONDERLA: el panel la lista igual y solo apaga su botón.
 *
 * ══ ACÁ NO ALCANZA CON UN SOLO CAMPO, al revés que en las otras tres ════════
 *
 * Aquellas comparan el nombre y listo porque un predio no tiene dos máquinas
 * llamadas igual. Un predio SÍ tiene varios cuarteles de la misma especie —el
 * cuadro medido trae dos filas "Cerezos"— así que comparar por especie apagaría
 * el botón de todo cuartel nuevo de una especie que ya está.
 *
 * La clave es ESPECIE + VARIEDAD + CUARTEL + AÑO + SUPERFICIE: cinco campos que
 * juntos identifican un paño.
 *
 * ══ EL AÑO ENTRÓ POR UN EXPEDIENTE REAL, NO POR PRECAUCIÓN ══════════════════
 *
 * Al principio eran TRES campos, y el caso que faltaba quedó anotado como
 * "costo aceptado": dos cuarteles de la misma especie y variedad sin nombre de
 * cuartel se verían iguales. Apareció en el PRIMER expediente real que se probó
 * (Fundo La Patagua, 14-09-2026), cuyo cuadro trae `Cerezos Santina, año 2019`
 * (72,93 há) y `Cerezos Santina, año 2020` (12,00 há) — dos paños distintos que
 * el documento separa por el año y nada más. Con tres campos, el segundo llegaba
 * con el botón apagado y sin explicación.
 *
 * ══ Y LA SUPERFICIE ENTRÓ EN LA MISMA CALIBRACIÓN, UN PASO DESPUÉS ══════════
 *
 * Con cuatro campos todavía colisionaban TRES filas del mismo cuadro real:
 *
 *     |Lapins|2019|15,50|2,0|x 4,5|1.111|
 *     |Lapins|2019| 6,27|2,0|x 4,5|1.111|
 *     |Lapins|2019| 9,31|2,0|x 4,5|1.111|
 *
 * Tres paños, misma especie, misma variedad, mismo año y sin nombre de cuartel.
 * Agregar el primero dejaba a los otros dos con el botón apagado.
 *
 * ESTO INVIERTE UNA DECISIÓN ANTERIOR, y conviene saber por qué. Se había
 * dejado la superficie FUERA con el argumento de que una cifra mal leída haría
 * pasar por nueva una fila que ya está. El argumento sigue siendo cierto; lo que
 * cambió es saber cuál de los dos errores ocurre de verdad:
 *
 * - el FALSO POSITIVO —bloquear un paño legítimo— pasó en el primer expediente
 *   real, sobre 2 de 3 filas, y deja a quien tasa sin entender por qué no puede
 *   agregarlas;
 * - el FALSO NEGATIVO —dejar pasar un duplicado— exige que el modelo lea mal una
 *   superficie, y cuando pasa el duplicado se VE: dos filas iguales, una al lado
 *   de la otra, en un cuadro que las agrupa por especie.
 *
 * NO ENTRA LA DENSIDAD NI EL MARCO: en ese cuadro son idénticos en las tres
 * filas, así que no distinguen nada y solo agregarían superficie de error.
 *
 * COSTO QUE QUEDA: dos cuarteles iguales en los CINCO campos siguen viéndose
 * como uno. En ese estado son de verdad indistinguibles, y quien tasa puede
 * cargar el segundo a mano desde el formulario.
 */
export function yaEstaEnElCuadroDePlantaciones(
  propuesta: Pick<
    PlantacionPropuesta,
    "especie" | "variedad" | "cuartel" | "anio_plantacion" | "superficie_ha"
  >,
  guardadas: PlantacionDeInforme[],
): boolean {
  const clave = claveDelPano(propuesta);

  return guardadas.some((plantacion) => claveDelPano(plantacion) === clave);
}

/**
 * Qué decir después del clic.
 *
 * EL VERBO ES "ENCONTRAMOS" Y NO "SE AGREGARON", igual que en las otras tres
 * pestañas con cuadro: acá todavía no se agregó nada, porque cada plantación se
 * agrega con su propio botón.
 */
export function resumenDeLoPropuestoEnPlantaciones(
  plantaciones: number,
  descripcion: boolean,
): string {
  if (plantaciones === 0 && !descripcion) {
    // SÍ SE EXPLICA, igual que en maquinaria y al revés que en instalaciones y
    // construcciones: el catálogo de documentos que se le pide al cliente no
    // trae ningún inventario de huertos —el texto de la carpeta interna
    // enumera construcciones, instalaciones y obras, sin nombrarlas—, así que
    // una propuesta vacía acá es lo NORMAL y no una señal de que algo falló.
    // Sin esta frase, quien la lea va a pensar que el botón no funcionó.
    return (
      "No encontramos plantaciones en los documentos leídos. Es lo habitual: "
      + "el expediente no incluye un inventario de huertos, así que este cuadro "
      + "se carga desde la visita a terreno."
    );
  }

  const partes: string[] = [];

  if (plantaciones > 0) {
    partes.push(`${plantaciones} ${plantaciones === 1 ? "plantación" : "plantaciones"}`);
  }

  if (descripcion) partes.push("la descripción general");

  return `Encontramos ${partes.join(" y ")}.`;
}

/** Un decimal del modelo, escrito como lo escribiría una persona. */
function decimal(valor: number | null): string {
  return valor === null ? "" : textoDeDecimal(String(valor));
}

/**
 * Especie + variedad + cuartel + año, normalizados. Ver `yaEstaEnElCuadroDe…`.
 *
 * REUSA `claveDeEspecie` PARA LOS TRES TEXTOS, aunque su nombre hable de una
 * sola: lo que hace es normalizar mayúsculas, tildes y espacios, que es
 * exactamente lo que hace falta acá. Copiarla habría dejado dos normalizaciones
 * que nada obliga a coincidir, y la del cuadro es la que decide cómo se AGRUPA
 * — el día que una de las dos cambie, la propuesta y el cuadro dejarían de
 * reconocer la misma fila sin que nada falle.
 *
 * EL AÑO NO PASA POR ELLA porque no es texto: se compara como número, y el
 * ausente cae en cadena vacía para que dos filas sin año sigan siendo la misma.
 */
function claveDelPano(fila: {
  especie: string;
  variedad: string | null;
  cuartel: string | null;
  anio_plantacion: number | null;
  superficie_ha: number | string | null;
}): string {
  const textos = [fila.especie, fila.variedad ?? "", fila.cuartel ?? ""].map(claveDeEspecie);

  return [...textos, fila.anio_plantacion ?? "", superficie(fila.superficie_ha)].join("|");
}

/**
 * La superficie como CIFRA y no como texto.
 *
 * Lo guardado llega del `decimal:2` de Eloquent —un string, "15.50"— y la
 * propuesta trae un número (15.5). Comparadas como texto serían distintas y la
 * misma fila se ofrecería de nuevo, esta vez duplicándola: justo el error que
 * esta comparación existe para evitar.
 */
function superficie(valor: number | string | null): string {
  if (valor === null || valor === "") return "";

  const n = typeof valor === "number" ? valor : Number(valor);

  return Number.isFinite(n) ? String(n) : "";
}
